'use client';

import '@fortawesome/fontawesome-svg-core/styles.css';
import { config as faconfig } from '@fortawesome/fontawesome-svg-core';
faconfig.autoAddCss = false;

import dynamic from 'next/dynamic';
import { ComponentType, PropsWithChildren, useEffect, useRef } from 'react';
import { Amplify } from 'aws-amplify';

import {
  EnvironmentContext,
  NextJSLinkWrapper,
  NextJSCustomImage,
  NextJSSEOWrapper,
  GenericActionContext,
  NotificationContainer,
  PlayerPortalsSiteSettings,
  PlayerPortalsConfigProvider,
} from '@r-and-a-shared-ui/common-ui';

import { SupportedDataSources } from '@r-and-a-shared-ui/core-data-mapping';
import {
  ComponentConfigContext,
  config as cmsConfig,
} from '@r-and-a-shared-ui/core-component-config';

import {
  PlayerPortalsActionsContext,
  ppFormCtaConfig,
} from '@r-and-a-shared-ui/common-forms-ui';

import { CommonUIActionsContext } from '@r-and-a-shared-ui/common-ui';

import { PPAuthProvider, checkForceAuth } from '@r-and-a-shared-ui/auth-client';

import { PPTrpcProvider } from '@r-and-a-shared-ui/randa-trpc-client/react';
import { ppTrpc } from '@r-and-a-shared-ui/randa-trpc-client/react';
import { awsExports } from '../../utils/aws-exports';

const PlayerPortalsConfiguration = dynamic(
  () =>
    import('@r-and-a-shared-ui/common-ui').then(
      m => m.PlayerPortalsConfiguration,
    ),
  { ssr: false },
);

const Providers = ({
  children,
  initialSiteSettings,
}: {
  children: React.ReactNode;
  initialSiteSettings?: PlayerPortalsSiteSettings;
}) => {
  const configured = useRef(false);

  if (!configured.current) {
    Amplify.configure(awsExports, { ssr: true });
    configured.current = true;
  }

  useEffect(() => {
    checkForceAuth();
  }, []);

  return (
    <PlayerPortalsConfigProvider initialSiteSettings={initialSiteSettings}>
      <PPAuthProvider awsExports={awsExports}>
        <EnvironmentContext.Provider
          value={{
            Link: NextJSLinkWrapper,
            Image: NextJSCustomImage,
            SEO: NextJSSEOWrapper,
          }}
        >
          <GenericActionContext.Provider
            value={{
              API: { KONTENT: ppTrpc.kontent },
              VIDEO_API: {},
            }}
          >
            <PlayerPortalsActionsContext.Provider
              value={{
                API: ppTrpc.playerPortalsFormApi,
                FormCTAConfig: ppFormCtaConfig,
              }}
            >
              <CommonUIActionsContext.Provider
                value={{
                  userDataAPI: ppTrpc.playerPortalsUserDataApi,
                  onSiteRegistrationAPI:
                    ppTrpc.playerPortalsOnSiteRegistrationApi,
                  formAPI: ppTrpc.playerPortalsFormApi,
                  preRegistrationAPI: ppTrpc.playerPortalsPreRegistrationApi,
                }}
              >
                <ComponentConfigContext.Provider
                  value={{
                    cmsBasedDataMapping: {
                      [SupportedDataSources.KontentAi]: cmsConfig,
                    },
                    CURRENT_CMS: SupportedDataSources.KontentAi,
                  }}
                >
                  {children}
                </ComponentConfigContext.Provider>
              </CommonUIActionsContext.Provider>
            </PlayerPortalsActionsContext.Provider>
          </GenericActionContext.Provider>
          <PlayerPortalsConfiguration siteSettings={initialSiteSettings} />
          <NotificationContainer />
        </EnvironmentContext.Provider>
      </PPAuthProvider>
    </PlayerPortalsConfigProvider>
  );
};

const withTrpc = <P extends PropsWithChildren>(Comp: ComponentType<P>) => {
  return function Wrapped(props: P) {
    return (
      <PPTrpcProvider>
        <Comp {...props} />
      </PPTrpcProvider>
    );
  };
};

export default withTrpc(Providers);
